import React, { useState, useEffect } from "react";
import { Drawer, Button, CircularProgress, Tooltip, FormControlLabel, Checkbox, IconButton } from "@mui/material";
import { ShoppingCartOutlined, CloseOutlined, Timer, ExpandLess, ExpandMore, CampaignOutlined } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { format } from "date-fns";
import { es } from "date-fns/locale";

import PurchaseSummary from "./components/PurchaseSummary";
import PaymentMethodSelector from "./components/PaymentMethodSelector";
import CampoCodigoCanal from "../../../../../../components/canales/CampoCodigoCanal";
import QRPaymentModal from "./components/QRPaymentModal";
import SecondDrawer from "../second-drawer/SecondDrawer";
import SelectorLugares from "../../../../../../components/compra/SelectorLugares";
import TarifasList from "./components/TarifasList";

import { usePaymentMethods } from "../../../../../../hooks/usePaymentMethods";
import { useCooldown } from "../../../../../../hooks/useCooldown";
import { useBarraInferior } from "../../../../../../hooks/useBarraInferior";
import { usePaymentProcessing } from "../../../../../../hooks/usePaymentProcessing";
import { useTermsAndConditions } from "../../../../../../hooks/useTermsAndConditions";
import { usePolling } from "../../../../../../hooks/usePolling";
import { useAuth } from "../../../../../../components/context/AuthContext";

import {
  calcularSubtotal,
  calcularComision,
  calculoSubtotalTarifa,
  formatCurrency,
  todasTarifasGratuitas,
  todasTarifasConAprobacion,
  hayTarifasConAprobacion,
  eventoTieneTarifasConAprobacion,
  hayTarifasPagas
} from "../../../../../../utils/paymentCalculations";

const FirstDrawerMobile = ({
  data,
  cantidad,
  setCantidad,
  toggleDrawer,
  openDrawer,
  setTarifasSeleccionadas,
  tarifasSeleccionadas,
  tarifasTicketsFiltrados,
  tarifasVouchersFiltrados,
  asientosSeleccionados,
  setAsientosSeleccionados,
}) => {
  const { isAuthenticated, loginWithRedirect } = useAuth();
  const param = useParams();
  const [codigoCanal, setCodigoCanal] = useState("");

  const [openSecondDrawer, setOpenSecondDrawer] = useState(false);
  const [dataTarifas, setDataTarifas] = useState([]);
  const [showResume, setShowResume] = useState(false);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [openSelectModal, setOpenSelectModal] = useState(false);
  const [tarifaModalActiva, setTarifaModalActiva] = useState(null);

  const {
    medioPago,
    setMedioPago,
    multiplesMetodosPago,
    mostrarBotonQR,
    mostrarBotonLink
  } = usePaymentMethods(data?.medios_pago, param.id);

  const {
    cooldownActive,
    cooldownRemaining,
    startCooldown,
    formatRemainingTime
  } = useCooldown();

  const {
    tieneLink,
    checkboxChecked,
    handleCheckboxChange
  } = useTermsAndConditions(data?.descripcion);

  const {
    startPolling,
    stopPolling,
    resetPolling,
    paymentStatus: pollingPaymentStatus
  } = usePolling();

  const {
    loadingCarrito,
    qrModalOpen,
    qrData,
    qrExpirationTime,
    handlePagoDirecto,
    handleCloseQrModal,
    formatQRImage
  } = usePaymentProcessing({
    data,
    cantidad,
    tarifasSeleccionadas,
    asientosSeleccionados,
    medioPago,
    param,
    setAsientosSeleccionados,
    setCantidad,
    setMostrarFormulario,
    setOpenSecondDrawer,
    startCooldown,
    startPolling,
    stopPolling,
    resetPolling,
    codigoCanal
  });

  useEffect(() => {
    if (!data?.tarifas) return;
    const seleccionadas = Object.entries(cantidad)
      .filter(([, cant]) => cant > 0)
      .map(([id, cant]) => {
        const tarifa = data.tarifas.find((t) => t.id === parseInt(id));
        return tarifa ? { ...tarifa, cantidad: cant } : null;
      })
      .filter(Boolean);
    setTarifasSeleccionadas(seleccionadas);
  }, [cantidad, data?.tarifas]);

  const subtotal = calcularSubtotal(data?.tarifas, cantidad, asientosSeleccionados);
  const comision = calcularComision(data?.tarifas, cantidad, asientosSeleccionados);
  const total = subtotal + comision;
  const hayComision = data?.tarifas?.some((tarifa) => {
    const cantidadTarifa = cantidad[tarifa.id] || 0;
    return cantidadTarifa > 0 && !tarifa?.asumir_comision;
  });

  const handleCloseDrawer = () => {
    const hasSelectedTarifas = Object.values(cantidad).some(value => value > 0);

    if (hasSelectedTarifas) {
      const userConfirmed = window.confirm(
        "¿Estás seguro de que quieres cerrar? Se perderán las tarifas seleccionadas."
      );
      if (!userConfirmed) return;
    }

    setMostrarFormulario(false);
    setOpenSecondDrawer(false);
    setCantidad({});
    setAsientosSeleccionados({});
    setTarifasSeleccionadas([]);
    toggleDrawer(false)();
  };

  const handleToggleResume = () => {
    setShowResume(!showResume);
  };

  const checkTarifasPreguntas = () => {
    return tarifasSeleccionadas.some(tarifa => {
      if (tarifa.informacion_adicional === false) return false;
      return tarifa.adicionales?.some(adicional => {
        return adicional.informacion_adicional.length > 0;
      });
    });
  };

  const handleButtonClick = () => {
    if (loadingCarrito || cooldownActive) return;

    if (checkTarifasPreguntas()) {
      setOpenSecondDrawer(true);
    } else {
      handlePagoDirecto();
    }
  };

  const toggleLoginorComprar = async () => {
    if (!isAuthenticated) {
      await loginWithRedirect({
        appState: { returnTo: window.location.pathname },
      });
    } else {
      toggleDrawer(true)();
    }
  };

  const handleSeleccionarUbicacion = (tarifa) => {
    setTarifaModalActiva(tarifa);
    setOpenSelectModal(true);
  };

  const handleConfirmarSeleccion = (seleccionMesa, datosEntradas = null, datosMenores = null) => {
    setCantidad(prev => ({
      ...prev,
      [tarifaModalActiva.id]: seleccionMesa.cantidad
    }));

    setAsientosSeleccionados(prev => ({
      ...prev,
      [tarifaModalActiva.id]: {
        asientos: seleccionMesa.asientos,
        mesas: seleccionMesa.mesas,
        metadata: seleccionMesa.metadata
      }
    }));

    if (datosMenores && datosMenores.cantidad > 0) {
      setCantidad(prev => ({
        ...prev,
        [datosMenores.tarifaId]: datosMenores.cantidad
      }));

      setAsientosSeleccionados(prev => ({
        ...prev,
        [datosMenores.tarifaId]: {
          asientos: datosMenores.asientos || [],
          mesas: {},
          metadata: {}
        }
      }));
    }

    if (datosEntradas && datosEntradas.agregarEntradas && datosEntradas.tarifaId) {
      const cantidadActual = cantidad[datosEntradas.tarifaId] || 0;
      setCantidad(prev => ({
        ...prev,
        [datosEntradas.tarifaId]: cantidadActual + datosEntradas.cantidad
      }));
    }

    setOpenSelectModal(false);
    setTarifaModalActiva(null);
  };

  const incremento = (idtarifa) => {
    setCantidad((prevCantidades) => {
      const nuevaCantidad = (prevCantidades[idtarifa] || 0) + 1;
      const tarifa = data?.tarifas?.find((tarifa) => tarifa.id === idtarifa);
      const limite = tarifa?.limite_persona || 5;
      if (nuevaCantidad > parseInt(limite)) return prevCantidades;
      return { ...prevCantidades, [idtarifa]: nuevaCantidad };
    });
  };

  const decremento = (idtarifa) => {
    setCantidad((prevCantidades) => ({
      ...prevCantidades,
      [idtarifa]: Math.max(0, (prevCantidades[idtarifa] || 0) - 1),
    }));
  };

  const necesitaSeleccionUbicacion = (tarifa) => {
    // 2 mesas, 3 filas, 4 filas y mesas
    return [2, 3, 4].includes(tarifa?.ubicacion?.tipo_ubicacion);
  };

  const getTextoBotonTarifa = (tarifa) => {
    const cantidadActual = cantidad[tarifa.id] || 0;

    if (necesitaSeleccionUbicacion(tarifa)) {
      if (cantidadActual > 0) {
        return `Modificar (${cantidadActual})`;
      } else {
        return "Seleccionar";
      }
    }

    return null;
  };

  const refBarraCompra = useBarraInferior();

  const hayTarifasDisponibles =
    (tarifasTicketsFiltrados && tarifasTicketsFiltrados.length > 0) ||
    (tarifasVouchersFiltrados && tarifasVouchersFiltrados.length > 0);

  return (
    <>
      <div ref={refBarraCompra} className="p-4 rounded-top container-btn-compra">
        <Button
          id="btn-compra"
          onClick={toggleLoginorComprar}
          className="p-4 d-flex gap-1 rounded"
          aria-label="ver-tarifas"
        >
          <ShoppingCartOutlined /> {eventoTieneTarifasConAprobacion(data.tarifas) ? "Solicitar acreditación" : "Comenzar compra"}
        </Button>
      </div>

      <Drawer
        sx={{
          "& .MuiDrawer-paper": {
            backgroundColor: "#f8f8ff",
            borderRadius: "10px 10px 0 0",
            maxHeight: "95vh",
            height: "auto",
            zIndex: 1200,
          },
        }}
        transitionDuration={500}
        anchor="bottom"
        open={openDrawer}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            handleCloseDrawer();
          }
        }}
      >
        {!mostrarFormulario && (
          <IconButton
            sx={{ position: "absolute", right: "0" }}
            onClick={handleCloseDrawer}
            aria-label="close"
            size="small"
          >
            <CloseOutlined style={{ color: "#221a60" }} />
          </IconButton>
        )}

        <div
          className="p-4 d-flex flex-column justify-content-between gap-2"
          style={{
            minHeight: showResume ? "50vh" : "30vh",
            maxHeight: "75vh",
            overflowY: "auto",
            paddingTop: "40px"
          }}
        >
          {!showResume ? (
            <>
              <div className="text-center mb-2">
                <p className="m-0 fs-3 fw-bold" style={{ color: "#221a60" }}>
                  Tarifas
                </p>
              </div>

              {!hayTarifasDisponibles ? (
                <div className="d-flex align-items-center flex-column ">
                  <div
                    className="contenedor-badge px-4 py-2"
                    style={{
                      background: "#dadaff",
                      color: "#3d2eab",
                      fontSize: "24px",
                    }}
                  >
                    <CampaignOutlined
                      sx={{
                        width: "45px",
                        height: "45px",
                        marginRight: "22px",
                      }}
                    />
                    <div className="d-flex flex-column">
                      <small className="fw-bold" style={{ fontSize: "18px" }}>
                        ¡Próxima apertura!
                      </small>
                      <small style={{ fontSize: "14px" }}>
                        Estamos trabajando en algo único para ti. ¡Esperalo!
                      </small>
                    </div>
                  </div>
                </div>
              ) : (
                <TarifasList
                  tarifas={data.tarifas}
                  cantidad={cantidad}
                  setCantidad={setCantidad}
                  asientosSeleccionados={asientosSeleccionados}
                  setTarifasSeleccionadas={setTarifasSeleccionadas}
                  handleSeleccionarUbicacion={handleSeleccionarUbicacion}
                  incremento={incremento}
                  decremento={decremento}
                  necesitaSeleccionUbicacion={necesitaSeleccionUbicacion}
                  getTextoBotonTarifa={getTextoBotonTarifa}
                />
              )}
            </>
          ) : (
            <PurchaseSummary
              tarifasSeleccionadas={tarifasSeleccionadas}
              cantidad={cantidad}
              calculoSubtotalTarifa={(tarifa) => calculoSubtotalTarifa(tarifa, cantidad, asientosSeleccionados)}
              subtotal={subtotal}
              comision={comision}
              hayComision={hayComision}
              total={total}
              formatCurrency={formatCurrency}
              isMobile={true}
            />
          )}

          {hayTarifasDisponibles && data?.tarifas?.length > 0 && (
            <div className="d-flex flex-column">
              <p
                className="mb-2 fs-3 fw-medium align-self-end"
                onClick={handleToggleResume}
                style={{ cursor: "pointer" }}
              >
                {!showResume ? <ExpandLess /> : <ExpandMore />} ${" "}
                {formatCurrency(total)}
              </p>
              <small className="mb-2 align-self-end">
                {format(new Date(), "E. d 'de' MMM. 'de' u HH:mm 'hs'", { locale: es })}
              </small>

              {tieneLink && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checkboxChecked}
                      onChange={handleCheckboxChange}
                      required
                      size="small"
                    />
                  }
                  label={<small>Confirmo haber leído y acepto los términos y condiciones del organizador del evento</small>}
                />
              )}

              <CampoCodigoCanal eventoId={param.id} value={codigoCanal} onChange={setCodigoCanal} />

              <PaymentMethodSelector
                medioPago={medioPago}
                setMedioPago={setMedioPago}
                mediosPago={data?.medios_pago}
                eventoId={param.id}
                mostrarBotonLink={mostrarBotonLink}
                mostrarBotonQR={mostrarBotonQR}
                isMobile={true}
                todasGratuitas={todasTarifasGratuitas(tarifasSeleccionadas, cantidad)}
                todasConAprobacion={todasTarifasConAprobacion(tarifasSeleccionadas, cantidad)}
                hayAprobacion={hayTarifasConAprobacion(tarifasSeleccionadas, cantidad)}
              />

              <Tooltip
                title={cooldownActive ? `Debes esperar antes de crear otro link de pago (${formatRemainingTime(cooldownRemaining)})` : ""}
                arrow
                placement="top"
              >
                <span style={{ width: '100%' }}>
                  <Button
                    id="btn-compra"
                    onClick={handleButtonClick}
                    className="p-4 rounded d-flex gap-1 justify-content-center"
                    aria-label="iniciar-compra"
                    disabled={
                      (tieneLink && !checkboxChecked) ||
                      (multiplesMetodosPago && !medioPago && hayTarifasPagas(tarifasSeleccionadas, cantidad)) ||
                      loadingCarrito ||
                      cooldownActive
                    }
                    style={{ width: '100%' }}
                  >
                    {loadingCarrito ? (
                      <>
                        <CircularProgress size="18px" sx={{ color: "white", marginRight: "10px" }} />
                        <span>Procesando...</span>
                      </>
                    ) : cooldownActive ? (
                      <>
                        <Timer sx={{ marginRight: "5px" }} />
                        <span>Espera {formatRemainingTime(cooldownRemaining)}</span>
                      </>
                    ) : (
                      <span>
                        <ShoppingCartOutlined />{" "}
                        {todasTarifasConAprobacion(tarifasSeleccionadas, cantidad)
                          ? (checkTarifasPreguntas() ? "Continuar" : "Solicitar acreditación")
                          : todasTarifasGratuitas(tarifasSeleccionadas, cantidad)
                            ? "Obtener tickets"
                            : (checkTarifasPreguntas() ? "Continuar con la compra" : "Finalizar compra")
                        }
                      </span>
                    )}
                  </Button>
                </span>
              </Tooltip>
            </div>
          )}
        </div>
      </Drawer>

      <SelectorLugares
        open={openSelectModal}
        onClose={() => {
          setOpenSelectModal(false);
          setTarifaModalActiva(null);
        }}
        tarifa={tarifaModalActiva}
        cantidadSeleccionada={cantidad[tarifaModalActiva?.id] || 1}
        onConfirmar={handleConfirmarSeleccion}
        seleccionActual={
          tarifaModalActiva
            ? (asientosSeleccionados[tarifaModalActiva.id] || { asientos: [], mesas: {} })
            : { asientos: [], mesas: {} }
        }
        tarifasTicketsFiltrados={tarifasTicketsFiltrados}
        cantidad={cantidad}
        asientosSeleccionados={asientosSeleccionados}
        setCantidad={setCantidad}
        setAsientosSeleccionados={setAsientosSeleccionados}
      />

      <SecondDrawer
        onBack={() => setOpenSecondDrawer(false)}
        handlePagoDirecto={handlePagoDirecto}
        dataTarifas={dataTarifas}
        setDataTarifas={setDataTarifas}
        loadingCarrito={loadingCarrito}
        cantidad={cantidad}
        open={openSecondDrawer}
        onClose={handleCloseDrawer}
        setOpenSecondDrawer={setOpenSecondDrawer}
        tarifasSeleccionadas={tarifasSeleccionadas}
      />

      <QRPaymentModal
        open={qrModalOpen}
        onClose={handleCloseQrModal}
        qrData={qrData}
        paymentStatus={pollingPaymentStatus}
        qrExpirationTime={qrExpirationTime}
        paymentReference={qrData?.reference || qrData?.referencia}
        formatRemainingTime={formatRemainingTime}
        formatQRImage={formatQRImage}
      />
    </>
  );
};

export default FirstDrawerMobile;